// must not include client-only files - only common files
import { PermissionsApiPayload } from './api_types.ts';
import { DateString } from './date_string.ts';
import {
  ActiveOrgCounts,
  AppHashes,
  ClientOrg,
  EmailInfo,
  EventSongList,
  Favorite,
  Household,
  HouseholdSurveyResponses,
  Huddle,
  HuddleGitHubTopTen,
  HuddleOrgDashboard,
  HuddleOrgNotes,
  HymnalManifest,
  IntervalSongList,
  Issue,
  Org,
  OrgBase,
  OrgHymnal,
  OrgId,
  OrgInfo,
  RestoreResult,
  SongList,
  SongListId,
  SongListType,
  SongNumber,
  SongRequest,
  SongSlug,
  SongViewWithOrg,
  StripeSubscriptionLevel,
  User,
} from './model.ts';
import { getHymnalManifestUrl, parseSongSlug } from './paths.ts';
import { Optional } from './types.ts';
import { ensureExists, naturalStringCompare } from './util.ts';

// PUT vs. POST (https://stackoverflow.com/questions/630453/what-is-the-difference-between-post-and-put-in-http)
//   PUT = idempotent, create or update (like upsert) -> lean toward PUT in most cases
//   POST - only update, not create

// the dedicated path the live-update WebSocket is served from (GitHub issue #3265); dev, preview,
// test and production all serve it from the page origin, so the client needs no host configuration
export const liveUpdatesSocketPath = '/ws';
// how often the server sends a heartbeat on that socket; the client's watchdog is derived from it so
// the two can never drift apart (production sits behind a load balancer whose idle timeout is about a
// minute and not configurable, so this has to stay well under it)
export const liveUpdateHeartbeatMs = 25_000;

// must be registered before accessing server APIs
export type TokenCallback = () => string | undefined;
export interface TokenCallbacks {
  userCallback: TokenCallback | undefined;
  householdCallback: TokenCallback | undefined;
}
let _tokenCallbacks: TokenCallbacks | undefined;
export function registerUserTokenCallbacks(callbacks: TokenCallbacks | undefined): TokenCallbacks | undefined {
  const previousCallbacks = _tokenCallbacks;
  _tokenCallbacks = callbacks;
  return previousCallbacks;
}

function getCurrentUserToken(): string | undefined {
  return ensureExists(_tokenCallbacks?.userCallback, 'call registerUserTokenCallbacks before using server_api')();
}

function getCurrentHouseholdToken(): string | undefined {
  return ensureExists(_tokenCallbacks?.householdCallback, 'call registerUserTokenCallbacks before using server_api')();
}

// for calling from the server
export type FetchOverride = (url: string, init?: RequestInit) => Promise<Response>;
let _fetchOverride: FetchOverride | undefined;
export function setFetchOverride(fetchOverride: FetchOverride) {
  _fetchOverride = fetchOverride;
}

export async function upsertUser(user: Optional<User, 'token'>): Promise<{ token: string }> {
  const response = await performFetch('/api/users', {
    method: 'POST',
    headers: _makeHeaders({ isHousehold: true }),
    body: JSON.stringify({ user }),
  });
  const { token } = (await response.json()) as { token: string };
  return { token };
}

export async function deleteUser(user: User): Promise<void> {
  await performFetch('/api/users', {
    method: 'DELETE',
    headers: _makeHeaders({ isHousehold: true }),
    body: JSON.stringify({ user }),
  });
}

/**
 * Thrown by server_api calls when the server answers with a non-2xx status. The message is the same
 * `Network error for request "<url>" (<status> - <statusText>): ...` text as before (code still matches
 * on it); `status` and `responseText` let callers classify the failure without string matching
 * (issue #3609). A request that never reaches the server (offline, DNS, CORS) rethrows fetch's own error
 * and carries no status.
 */
export class HttpError extends Error {
  readonly status: number;
  readonly responseText: string;

  constructor(message: string, status: number, responseText: string) {
    super(message);
    this.status = status;
    this.responseText = responseText;
  }
}

function makeNetworkErrorMessage(url: string, message: string, response?: Response) {
  const responseClause = response ? ` (${response.status} - ${response.statusText})` : '';
  return `Network error for request "${url}"${responseClause}: ${message}`;
}

async function performFetch(url: string, init?: RequestInit): Promise<Response> {
  let response: Response;
  if (!_fetchOverride) {
    throw new Error(`Must call setFetchOverride before using server_api`);
  }
  try {
    // @ts-ignore
    response = await (_fetchOverride ?? fetch)(url, init);
  } catch (e: any) {
    console.error(makeNetworkErrorMessage(url, e.message + '\n' + e.stack));
    throw e;
  }
  if (!response.ok) {
    const responseText = await response.text();
    const message = responseText + '\n' + new Error().stack;
    // the response body carries the server's reason - without it a diagnostic log shows only the status (issue #3609)
    console.error(
      `performFetch ${init?.method ?? 'GET'} ${url}, response: ${response.status} ${responseText.slice(0, 500)}, Stack:\n` +
        new Error().stack,
    );
    throw new HttpError(makeNetworkErrorMessage(url, message, response), response.status, responseText);
  }
  return response;
}

export async function getHouseholdOrgs(householdToken: string): Promise<ClientOrg[]> {
  const response = await performFetch(`/api/households/${encodeURIComponent(householdToken)}`, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  const household = (await response.json()) as Household;
  const clientOrgs = (await Promise.all(household.orgIds.map((orgId) => getOrg(orgId))))
    .filter((org) => !!org)
    .sort((lhs, rhs) => naturalStringCompare(lhs.type, rhs.type));
  return clientOrgs;
}

export async function setPermissions(payload: PermissionsApiPayload) {
  // anonymous is allowed because the invite-link flow authorizes via the orgAdminToken secret
  await performFetch(`/api/permissions`, {
    method: 'PUT',
    headers: _makeHeaders({ allowAnonymousUser: true }),
    body: JSON.stringify(payload),
  });
}

export async function revokePermissions(payload: PermissionsApiPayload) {
  await performFetch(`/api/permissions`, {
    method: 'DELETE',
    headers: _makeHeaders({ allowAnonymousUser: true }),
    body: JSON.stringify(payload),
  });
}

export async function generateOrgSingingPlans({
  forDate,
  forOrgId,
}: {
  forDate: DateString;
  forHouseholdToken?: string;
  forOrgId?: number;
}) {
  let url = `/api/households/generate-org-singing-plans?date=${forDate}`;
  if (forOrgId) {
    url += `&orgId=${forOrgId}`;
  }
  const result = await performFetch(url, { method: 'PUT', headers: _makeHeaders() });
  return await result.text();
}

export async function calculateAssignmentCompletion({ forDate }: { forDate: DateString }) {
  const url = `/api/households/calculate-assignment-completion?date=${forDate}`;
  const result = await performFetch(url, { method: 'PUT', headers: _makeHeaders() });
  return await result.text();
}

export async function upsertOrg(org: Partial<Org> & { id: OrgId }): Promise<{ id: number }> {
  if ((org as ClientOrg).mostRecentSyncTimestamp || (org as ClientOrg).updates) {
    console.error(`Server Org should not contain ClientOrg info: ${JSON.stringify(org)}`);
  }
  // strip ClientOrg info (should not be necessary, but `updates` snuck in somehow 8/2025)
  const { mostRecentSyncTimestamp: _unused1, updates: _unused2, ...serverOrg } = org as ClientOrg;
  const response = await performFetch('/api/orgs', {
    method: 'PUT',
    headers: _makeHeaders(),
    body: JSON.stringify({
      org: {
        ...serverOrg,
        ...(serverOrg.becameOrgId === undefined && 'becameOrgId' in serverOrg && { becameOrgId: null }),
      },
    }),
  });
  const { id } = (await response.json()) as { id: string };
  return { id: Number(id) };
}

// TODO(hewitt): accept a search string so we don't return all 7K orgs
// NOTE: Does not include church hymnals -> should only return bare bones, then fill in by calling getChurch
export async function getAllOrgs(): Promise<OrgBase[]> {
  const response = await performFetch('/api/orgs', {
    method: 'GET',
    headers: _makeHeaders({ allowAnonymousUser: true }),
  });
  return (await response.json()) as OrgBase[];
}

export async function getOrg(id: number): Promise<ClientOrg | undefined> {
  const response = await performFetch(`/api/orgs/${id}`, {
    method: 'GET',
    headers: _makeHeaders({ allowAnonymousUser: true }),
  });
  const org = (await response.json()) as ClientOrg | null;
  return org ?? undefined;
}

export async function deleteOrg(org: ClientOrg, { purge }: { purge?: boolean } = {}): Promise<void> {
  await performFetch('/api/orgs', {
    method: 'DELETE',
    headers: _makeHeaders(),
    body: JSON.stringify({ org, purge }),
  });
}

export async function getDaysSinceLastSong(orgId: number): Promise<number | undefined> {
  const response = await performFetch(`/api/orgs/${orgId}/days-since-last-song`, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  const daysSinceLastSong = (await response.json()) as number | null;
  if (daysSinceLastSong === null) {
    return undefined;
  }
  return daysSinceLastSong;
}

export async function getOrgInfo(orgId: number, date: DateString): Promise<OrgInfo | undefined> {
  const response = await performFetch(`/api/orgs/${orgId}/info?date=${date}`, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return (await response.json()) as OrgInfo;
}

export async function getActiveOrgCounts(): Promise<ActiveOrgCounts> {
  const response = await performFetch(`/api/orgs/active-org-counts`, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return (await response.json()) as ActiveOrgCounts;
}

// internal only - a pure read: the rotation advances on a server job, weekdays at 2 AM Mountain
export async function getHuddle(): Promise<Huddle> {
  const response = await performFetch('/api/huddle', {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return (await response.json()) as Huddle;
}

// separate from getHuddle so a slow or failing GitHub never holds up the prayer focus cards
export async function getHuddleGitHubTopTen(): Promise<HuddleGitHubTopTen> {
  const response = await performFetch('/api/huddle/github', {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return (await response.json()) as HuddleGitHubTopTen;
}

// The dashboard embedded under the huddle's active church card.  Separate from getOrgInfo on
// purpose: that response carries household tokens, user tokens and the org's adminToken, and this
// one carries none of them.
export async function getHuddleOrgDashboard(orgId: OrgId): Promise<HuddleOrgDashboard> {
  const response = await performFetch(`/api/huddle/org-dashboard?orgId=${orgId}`, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return (await response.json()) as HuddleOrgDashboard;
}

// also separate from getHuddle - Google Drive must never hold up the prayer focus cards
export async function getHuddleOrgNotes(orgId: OrgId): Promise<HuddleOrgNotes> {
  const response = await performFetch(`/api/huddle/org-notes?orgId=${orgId}`, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return (await response.json()) as HuddleOrgNotes;
}

export function stringFromOrg(org: Pick<ClientOrg, 'name' | 'location'>): string {
  return `${org.name} (${org.location})`;
}

export async function getIsInternalUser(): Promise<boolean> {
  if (!getCurrentUserToken()) {
    return false;
  }
  const response = await performFetch('/api/users/self/internal', {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return (await response.json()) as boolean;
}

// TODO(hewitt): pass household token, etc
// export async function uploadChurchCustomMusicFile(churchId: number, filename: string): Promise<Response> {
//   const response = await performFetch(`/api/orgs/${orgId}/custom-music/${filename}`, {
//     method: 'PUT',
//     headers: _makeHeaders(),
//   });
//   return response;
// }

// TODO(hewitt): This API is vulnerable!  Pass user/household token eventually to make this API secure!
export async function uploadOrgCustomMusicFile({
  orgId,
  songName,
  songNumber,
  psalmNumber,
  file,
}: {
  orgId: number;
  songName?: string;
  songNumber?: string;
  psalmNumber?: number;
  file: File;
}): Promise<SongNumber> {
  if (!songName && !songNumber) {
    throw new Error(`Either song name or song number is required`);
  }
  const formData = new FormData();
  if (songName) {
    formData.append('songName', songName);
  }
  if (songNumber) {
    formData.append('songNumber', songNumber);
  }
  if (psalmNumber) {
    formData.append('psalmNumber', psalmNumber.toString());
  }
  formData.append('file', file);

  // TODO(hewitt): Was unable to push form data through standard `fetch` API...
  // await performFetch('/api/upload-song', {
  //   method: 'POST',
  //   headers: _makeHeaders(),
  //   body: formData,
  // });

  const url = `/api/orgs/${orgId}/custom-music`;
  const request = new XMLHttpRequest();
  request.open('POST', url, true);
  request.setRequestHeader('Authorization', `Bearer ${getCurrentUserToken()}`);
  // TODO(hewitt): Set headers of XMLHttpRequest (wasn't working, but not currently necessary)
  // for (const [key, value] of Object.entries(_makeHeaders())) {
  //   request.setRequestHeader(key, value);
  // }
  return await new Promise((resolve, reject) => {
    request.onreadystatechange = () => {
      if (request.readyState === 4) {
        if (request.status === 200) {
          resolve(request.responseText as SongNumber);
        } else {
          const message = request.responseText;
          console.error(`Song upload failed: ${message}`);
          reject({ message });
        }
      }
    };
    request.send(formData);
  });
}

export async function deleteCustomSong(songSlug: SongSlug): Promise<void> {
  const { orgId, songNumber } = parseSongSlug(songSlug);
  await performFetch(`/api/orgs/${orgId}/custom-music/${songNumber}`, {
    method: 'DELETE',
    headers: _makeHeaders(),
  });
}

export async function renameCustomSong({
  songSlug,
  songName,
  psalmNumber,
}: {
  songSlug: SongSlug;
  songName: string;
  psalmNumber?: number;
}): Promise<void> {
  const { orgId, songNumber } = parseSongSlug(songSlug);
  await performFetch(`/api/orgs/${orgId}/custom-music/${songNumber}`, {
    method: 'PUT',
    headers: _makeHeaders(),
    body: JSON.stringify({ songName, psalmNumber }),
  });
}

export function getPdfUrlForSongList(orgId: number, date: DateString, type?: SongListType): string {
  return `/api/orgs/${orgId}/${date}/song-list.pdf${type ? `?type=${type}` : ''}`;
}

// TODO(hewitt): This is the correct way to identify an org admin, but is not useful without household membership
export async function isUserAdminForOrg(orgId: OrgId): Promise<boolean> {
  if (!getCurrentUserToken()) {
    return false;
  }
  const url = `/api/orgs/${orgId}/is-admin`;
  const response = await performFetch(url, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return await response.json();
}

export async function getOrgHymnal(orgId: OrgId): Promise<OrgHymnal> {
  const url = `/api/orgs/${orgId}/org-hymnal`;
  const response = await performFetch(url, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return await response.json();
}

export async function reportSongViews(householdToken: string | undefined, songViews: SongViewWithOrg[]): Promise<void> {
  await performFetch('/api/song-views', {
    method: 'POST',
    headers: _makeHeaders({ allowAnonymousUser: true }),
    body: JSON.stringify({ ...(householdToken && { householdToken }), songViews }),
  });
}

export async function addSongRequest(songRequest: SongRequest): Promise<void> {
  await performFetch('/api/song-requests', {
    method: 'POST',
    headers: _makeHeaders(),
    body: JSON.stringify({ songRequest }),
  });
}

export async function addIssue(issue: Issue): Promise<void> {
  await performFetch('/api/issues', {
    method: 'POST',
    headers: _makeHeaders({ allowAnonymousUser: true }),
    body: JSON.stringify({ issue }),
    // the page may be reloading right after it reports a dead IndexedDB connection (GitHub issue #3906):
    // keepalive lets the request finish anyway
    keepalive: true,
  });
}

export async function addFavorite(favorite: Favorite): Promise<void> {
  await performFetch('/api/users/self/favorites', {
    method: 'POST',
    headers: _makeHeaders(),
    body: JSON.stringify(favorite),
  });
}

export async function getFavorites(): Promise<Favorite[]> {
  const response = await performFetch('/api/users/self/favorites', {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return await response.json();
}

export async function removeFavorite(favorite: Favorite): Promise<void> {
  await performFetch('/api/users/self/favorites', {
    method: 'DELETE',
    headers: _makeHeaders(),
    body: JSON.stringify(favorite),
  });
}

export async function getAppHashes({ orgId }: { orgId?: number }): Promise<AppHashes> {
  const queryString = orgId ? `?orgId=${orgId}` : '';
  const response = await performFetch(`/api/app-hashes${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  return await response.json();
}

export async function upsertSongLists(orgId: number, songLists: SongList[]): Promise<SongListId[]> {
  const response = await performFetch('/api/song-lists', {
    method: 'POST',
    headers: _makeHeaders(),
    body: JSON.stringify({ orgId, songLists }),
  });
  return (await response.json()) as SongListId[];
}

export async function deleteSongLists(orgId: number, songListIds: number[]): Promise<void> {
  await performFetch('/api/song-lists', {
    method: 'DELETE',
    headers: _makeHeaders(),
    body: JSON.stringify({ orgId, songListIds }),
  });
}

export async function copySongListsAndTempos({
  oldChurchId,
  newChurchId,
}: {
  oldChurchId: number;
  newChurchId: number;
}) {
  let url = `/api/copy-song-lists?from=${oldChurchId}&to=${newChurchId}`;
  const result = await performFetch(url, { method: 'PUT', headers: _makeHeaders() });
  return await result.text();
}

export async function getCustomSongIntervals(orgId: number): Promise<IntervalSongList[]> {
  let url = `/api/custom-intervals?orgId=${orgId}`;
  const response = await performFetch(url, {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return await response.json();
}

export async function getSongLists({
  churchId,
  from,
  to,
}: {
  churchId: number;
  from?: DateString;
  to?: DateString;
}): Promise<SongList[]> {
  let url = `/api/song-lists?orgId=${churchId}` + (from ? `&from=${from}` : '') + (to ? `&to=${to}` : '');
  const response = await performFetch(url, {
    method: 'GET',
    headers: _makeHeaders({ allowAnonymousUser: true }),
  });
  return await response.json();
}

export async function getSeasonalSongLists(): Promise<EventSongList[]> {
  const response = await performFetch('/api/seasonal-song-lists', {
    method: 'GET',
    headers: _makeHeaders(),
  });
  return await response.json();
}

// A household deleting itself from the Help page, outright (GitHub issue #4001); the path is the
// route's pre-#4001 name. deleteHousehold below is the staff route.
export async function deleteMyHousehold({ token }: { token: string }) {
  await performFetch(`/api/billing/clear-household`, {
    method: 'POST',
    headers: _makeHeaders(),
    body: JSON.stringify({ token }),
  });
}

function _makeHeaders({
  allowAnonymousUser,
  isHousehold,
}: { allowAnonymousUser?: boolean; isHousehold?: boolean } = {}):
  { Authorization: string; 'Content-Type': string } | { 'Content-Type': string } {
  let token: string | undefined;
  let error: any;
  try {
    const userToken = getCurrentUserToken();
    token = isHousehold || !userToken ? getCurrentHouseholdToken() : userToken;
  } catch (e) {
    error = e;
  }
  if (token) {
    return getAuthorizationHeader(token);
  }
  if (allowAnonymousUser) {
    return getContentType();
  }
  if (error) {
    throw error;
  }
  throw new Error(`No user or household token available`);
}

function getAuthorizationHeader(token: string): { Authorization: string; 'Content-Type': string } {
  return {
    Authorization: `Bearer ${token}`,
    ...getContentType(),
  };
}

function getContentType(): { 'Content-Type': string } {
  return { 'Content-Type': 'application/json' };
}

export async function setOrgHymnals(orgId: number, hymnalIds: number[]) {
  await performFetch(`/api/orgs/${orgId}/hymnals`, {
    method: 'PUT',
    headers: _makeHeaders(),
    body: JSON.stringify({ hymnalIds }),
  });
}

export async function transferAllMemberHouseholds({
  fromOrgId,
  toOrgId,
}: {
  fromOrgId: number;
  toOrgId: number;
}): Promise<void> {
  await performFetch(`/api/orgs/${fromOrgId}/transfer-member-households`, {
    method: 'PUT',
    headers: _makeHeaders(),
    body: JSON.stringify({ toOrgId }),
  });
}

export async function getHousehold({ householdToken }: { householdToken: string }): Promise<Household | undefined> {
  const url = `/api/households/${encodeURIComponent(householdToken)}`;
  const response = await performFetch(url, { method: 'GET' });
  const household = (await response.json()) as Household | null;
  return household ?? undefined;
}

export async function optHouseholdIntoChurchSubscription({
  householdToken,
  churchToken,
}: {
  householdToken: string;
  churchToken: string;
}) {
  await performFetch(`/api/households/${householdToken}/opt-into-church-subscription`, {
    method: 'PUT',
    headers: _makeHeaders(),
    body: JSON.stringify({ churchToken }),
  });
}

export async function recordHouseholdSurveyResponses({
  householdToken,
  responses,
}: {
  householdToken: string;
  responses: HouseholdSurveyResponses;
}) {
  await performFetch(`/api/households/${householdToken}/survey-responses`, {
    method: 'PUT',
    headers: _makeHeaders(),
    body: JSON.stringify({ responses }),
  });
}

// TODO(hewitt): this call is insecure - see routes.ts for detail - we should send a link via email to join household
export async function getHouseholdToken({ householdEmail }: { householdEmail: string }): Promise<string | undefined> {
  const response = await performFetch(
    `/api/billing/get-household-token?householdEmail=${encodeURIComponent(householdEmail)}`,
  );
  const token = (await response.json()) as string;
  return token ?? undefined;
}

export async function createHousehold({
  familyLastName,
  householdEmail,
}: {
  familyLastName: string;
  householdEmail: string;
}): Promise<Household> {
  const response = await performFetch('/api/billing/create-household-new', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: householdEmail,
      familyLastName,
      householdEmail,
    }),
  });
  return (await response.json()) as Household;
}

export async function upsertHousehold(household: Household) {
  await performFetch(`/api/households/${encodeURIComponent(household.token)}`, {
    method: 'PUT',
    headers: _makeHeaders(),
    body: JSON.stringify({ household }),
  });
}

export async function deleteHousehold({ householdEmail }: { householdEmail: string }) {
  await performFetch(`/api/households`, {
    method: 'DELETE',
    headers: _makeHeaders(),
    body: JSON.stringify({ householdEmail }),
  });
}

// Asks the server to create a Stripe Checkout Session for the household and returns the session
// URL to navigate to. The server stamps the householdToken into the subscription metadata so the
// webhook can link the resulting subscription to the household (GitHub issue #3096).
// The server declined to start a checkout with the requested promotion (GitHub issue #3551):
// the household already subscribes, the code has been fully claimed, or the campaign is
// unknown. The message is written for the household and can be shown as is.
export class CheckoutRejectedError extends Error {}

export async function createStripeCheckoutSession({
  householdToken,
  level,
  promo,
}: {
  householdToken: string;
  level: StripeSubscriptionLevel;
  // promotional campaign slug from the landing URL (GitHub issue #3551)
  promo?: string;
}): Promise<string> {
  const url = '/api/stripe/create-checkout-session';
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ householdToken, level, ...(promo && { promo }) }),
  };
  if (!_fetchOverride) {
    throw new Error(`Must call setFetchOverride before using server_api`);
  }
  // 409 carries a household-facing explanation rather than a network failure, so it is read
  // before performFetch's generic non-OK handling
  const response = await _fetchOverride(url, init);
  if (response.status === 409) {
    throw new CheckoutRejectedError(await response.text());
  }
  if (!response.ok) {
    throw new Error(makeNetworkErrorMessage(url, await response.text(), response));
  }
  const { url: checkoutUrl } = (await response.json()) as { url: string };
  return checkoutUrl;
}

export async function updateHouseholdWithStripeSubscription({
  sessionId,
  householdToken,
}: {
  sessionId: string;
  householdToken: string;
}) {
  await performFetch('/api/stripe/update-household', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, householdToken }),
  });
}

// TODO(hewitt): Delete this function - favorites should be using our normal useHymnals machinery
export async function getHymnalManifest(hymnalName: string): Promise<HymnalManifest> {
  const hymnalManifestUrl = getHymnalManifestUrl(hymnalName);
  try {
    const response = await performFetch(hymnalManifestUrl);
    return (await response.json()) as HymnalManifest;
  } catch (e: any) {
    if ((e.message as string).includes('404')) {
      console.error(`Ignoring 404 error missing manifest for ${hymnalName}: ${e.message}`);
      return {};
    }
    throw e;
  }
}

export async function sendEmail<T extends EmailInfo>(emailInfo: T): Promise<void> {
  await performFetch('/api/send-email', {
    method: 'POST',
    headers: _makeHeaders({ isHousehold: true }),
    body: JSON.stringify({ emailInfo }),
  });
}

export async function backupSongLists(): Promise<void> {
  await performFetch('/api/backup-song-lists', {
    method: 'POST',
    headers: _makeHeaders(),
  });
}

export async function restoreSongLists(): Promise<RestoreResult> {
  const response = await performFetch('/api/restore-song-lists', {
    method: 'POST',
    headers: _makeHeaders(),
  });
  return (await response.json()) as RestoreResult;
}
